// Program Search /////////////////////////////////////////////////////////////
//
// This file controls the submission tools for search.
//
// App -> Search Container -> Search Form -> Program Search
///////////////////////////////////////////////////////////////////////////////

// SETUP //////////////////////////////////////////////////////////////////////
// Third-party imports.
import React, { useState, useEffect, } from 'react'
import { Button, Col, Row, Form, Input, InputGroup, } from 'reactstrap'

// Local imports.
import { PROGRAM_SEARCH_TYPES, } from '../../../constants/other'
import mapActions from '../../../containers/SearchMapActionTypes'
import ProgramSearchCategories from './ProgramSearchCategories'
import ProgramSearchFilters from './ProgramSearchFilters'
import PropTypes from 'prop-types'

// Constants.
const noop = () => {}
const dispatchSig = (action) => {}
const ProgramSearchMain = ({ basicFilter, locationFilter, onBasicChanged, onLocationChanged, }) => {
  return <Row className={'mt-3 mb-1'}>
    <Col xs={12}>
      <InputGroup className={'search-input-group'}> 
        <i className={'fa fa-search occupation-search-icon'}></i>
        <Input
          type={'text'}
          placeholder={'Search by Program, Occupation, or School'}
          name={'search-basic'}
          id={'txt-search-basic'}
          value={basicFilter}
          onChange={e => onBasicChanged(e.target.value)}
          label={'Keyword'}
          aria-label={'Search by Keyword'}
          className={'occupation-search-input occupation-search-main occupation-search-placeholder'}
        />
        <i className={'fa fa-map-marker-alt location-search-icon'}></i>
        <Input
          type={'text'} 
          placeholder={'Address, Street, City, or Zip'} 
          name={'search-location'}
          id={'txt-search-location'}
          value={locationFilter} 
          onChange={e => onLocationChanged(e.target.value)} 
          label={'Location'}
          aria-label={'Search by Location'}
          className={'occupation-search-input location-search-main occupation-search-placeholder'}
        />

        <Button name={'btn-program-search'} id={'btn-program-search'} className={'btn-lg'}>Search</Button>
      </InputGroup>
    </Col>
  </Row>
}

// HELPER FUNCTIONS ///////////////////////////////////////////////////////////


// MAIN FUNCTION //////////////////////////////////////////////////////////////
const ProgramSearch = ({
  counties = [],
  eligibilityRequirements = [],
  industryTags = [],
  isLargeDisplay = false,
  isMobileDisplay = false,
  mapDispatch = dispatchSig,
  minimumEducationRequirements = [],
  mobileMapShowing,
  onSearchClick = noop,
  parentRef,
  programLevels = [],
  programTypes = [],
  searchFilters,
  setTypeFilter = noop,
  typeFilter = 'all',
  userLocation,
}) => {
  const [ basicFilter, setBasicFilter, ] = useState('')
  const [ cityFilter, setCityFilter, ] = useState('')
  const [ countyFilter, setCountyFilter, ] = useState([])
  const [ eligibilityRequirementFilter, setEligibilityRequirementFilter, ] = useState([])
  const [ industryTagFilter, setIndustryTagFilter, ] = useState([])
  const [ locationFilter, setLocationFilter, ] = useState('')
  const [ minimumAgeFilter, setMinimumAgeFilter, ] = useState('')
  const [ minimumEducationRequirementFilter, setMinimumEducationRequirementFilter, ] = useState([])
  const [ programLevelFilter, setProgramLevelFilter, ] = useState([])
  const [ programTypeFilter, setProgramTypeFilter, ] = useState([])
  const [ radiusFilter, setRadiusFilter, ] = useState('')
  const [ schoolNameFilter, setSchoolNameFilter, ] = useState('')
  const [ wageMaxFilter, setWageMaxFilter, ] = useState('')
  const [ wageMinFilter, setWageMinFilter, ] = useState('')
  const [ zipCodeFilter, setZipCodeFilter, ] = useState('')
 
  // CALLBACK FUNCTIONS ///////////////////////////////////////////////////////
  const onToggleListOrMap = React.useCallback(() => {
    if (mapDispatch) 
      mapDispatch(mapActions.toggleMobileMap())
  }, [ mapDispatch, ])

  // USE EFFECT ///////////////////////////////////////////////////////////////
  // Set filter states whenever the filters change.
  useEffect(() =>{
    if (searchFilters) {
      // Set each filter value using its corresponding "set state" function.
      const filterFunctionPairs = [
        (searchFilters['basic'], setBasicFilter),
        (searchFilters['location'], setLocationFilter),
        (searchFilters['city'], setCityFilter),
        (searchFilters['institution'], setSchoolNameFilter),
        (searchFilters['wageMax'], setWageMaxFilter),
        (searchFilters['wageMin'], setWageMinFilter),
        (searchFilters['zipcode'], setZipCodeFilter),
        (searchFilters['programType'], setProgramTypeFilter),
        (searchFilters['minimumAge'], setMinimumAgeFilter),
        (searchFilters['radius'], setRadiusFilter),
      ]
      for (const pair of filterFunctionPairs) {
        const inputValue = pair[0]
        const setterFunction = pair[1]
        if (inputValue)
          setterFunction(inputValue)
      }
    }
    else {
      // Do nothing.
    }
  }, [ searchFilters, ])

  // SPECIAL //////////////////////////////////////////////////////////////////
  const doSearch = (e) => {
    e.preventDefault()
    const filters = {}
    if (basicFilter) 
      filters['basic'] = basicFilter
    if (typeFilter != null && typeFilter !== undefined) 
      filters['typeFilter'] = typeFilter
    if (locationFilter != null && locationFilter !== undefined) 
      filters['location'] = locationFilter
    if (countyFilter != null && countyFilter !== undefined && countyFilter.length > 0) 
      filters['counties'] = countyFilter.map((item) => { return item.id }).join(',')
    if (cityFilter != null && cityFilter !== undefined) 
      filters['city'] = cityFilter
    if (schoolNameFilter != null && schoolNameFilter !== undefined) 
      filters['school_name'] = schoolNameFilter
    
    if (wageMaxFilter != null && wageMaxFilter !== undefined) {
      const wageMaxFloat = Number.parseFloat(wageMaxFilter)
      if (!isNaN(wageMaxFloat)){
        filters['wage_max'] = wageMaxFloat
      }
    }
    if (wageMinFilter != null && wageMinFilter !== undefined) {
      const wageMinFloat = Number.parseFloat(wageMinFilter)
      if (!isNaN(wageMinFloat)) {
        filters['wage_min'] = wageMinFloat
      }
    }
    if (zipCodeFilter != null && zipCodeFilter !== undefined) 
      filters['zipcode'] = zipCodeFilter
    
    if (programTypeFilter != null && programLevelFilter !== undefined && programTypeFilter.length > 0) 
      filters['program_types'] = programTypeFilter.map((item) => { return item.id }).join(',')
    if (programLevelFilter != null && programLevelFilter !== undefined && programLevelFilter.length > 0) 
      filters['program_levels'] = programLevelFilter.map((item) => { return item.id }).join(',')
    if (eligibilityRequirementFilter != null && eligibilityRequirementFilter !== undefined && eligibilityRequirementFilter.length > 0) 
      filters['eligibility_requirements'] = eligibilityRequirementFilter.map((item) => { return item.id }).join(',')
    if (minimumEducationRequirementFilter != null && minimumEducationRequirementFilter !== undefined && minimumEducationRequirementFilter.length > 0) 
      filters['minimum_education_requirements'] = minimumEducationRequirementFilter.map((item) => { return item.id }).join(',')
    
    if (minimumAgeFilter != null && minimumAgeFilter !== undefined) {
      const minimumAgeInt = Number.parseInt(minimumAgeFilter)
      if (!isNaN(minimumAgeInt)){
        filters['minimum_age'] = minimumAgeInt
      }
    }
    if (industryTagFilter != null && industryTagFilter !== undefined && industryTagFilter.length > 0) 
      filters['industry_tags'] = industryTagFilter.map((item) => { return item.id }).join(',')
    if (userLocation != null && userLocation !== undefined && userLocation.coords != null && userLocation.coords !== undefined) 
      filters['current_location'] = { 'lat': userLocation.coords.latitude, 'lon': userLocation.coords.longitude, }
    if (radiusFilter != null && radiusFilter !== undefined && radiusFilter.length > 0) {
      const radiusFloat = Number.parseFloat(radiusFilter)
      if (!isNaN(radiusFloat)) { 
        filters['radius'] = Number.parseFloat(radiusFilter)
      }
    }
    onSearchClick(filters)
  }

  // RETURN HTML //////////////////////////////////////////////////////////////
  return <Form onSubmit={doSearch}>
    <Row className={'search-inner-row'}>
      <Col className={'bg-white px-4'} xs={12}>
        <ProgramSearchMain 
          basicFilter={basicFilter}
          onBasicChanged={setBasicFilter}
          locationFilter={locationFilter}
          onLocationChanged={setLocationFilter}
        />
      </Col>
    </Row>
    <Row className={'search-inner-row position-sticky sticky-top pb-0 pt-3'} >
      <Col xs={10} md={10} className={'bg-white'}>
        <ProgramSearchCategories
          types={PROGRAM_SEARCH_TYPES}
          activeType={typeFilter}
          setType={setTypeFilter}
          isMobileDisplay={isMobileDisplay}
        />
      </Col>
      <Col xs={2} className={'bg-white align-self-center pl-0 pr-0'}>
        <a href={'https://careerconnectwa.org/contact-us/'} target="_blank" className={'btn btn-inverted pb-1 pt-1 pr-1'} title={'Click here to email Career Connect Washington with any questions or concerns'}>
          <i className={'fa fa-question-circle'}></i> {isMobileDisplay ? null : '\u00A0Help'}
        </a>
      </Col>
      <Col xs={12} className={'p-0'}>
        <ProgramSearchFilters
          counties={counties}
          programTypes={programTypes}
          programLevels={programLevels}
          eligibilityRequirements={eligibilityRequirements}
          minimumEducationRequirements={minimumEducationRequirements}
          industryTags={industryTags}
          countyFilter={countyFilter}
          setCountyFilter={setCountyFilter}
          schoolNameFilter={schoolNameFilter}
          setSchoolNameFilter={setSchoolNameFilter}
          cityFilter={cityFilter}
          setCityFilter={setCityFilter}
          programTypeFilter={programTypeFilter}
          setProgramTypeFilter={setProgramTypeFilter}
          programLevelFilter={programLevelFilter}
          setProgramLevelFilter={setProgramLevelFilter}
          eligibilityRequirementFilter={eligibilityRequirementFilter}
          setEligibilityRequirementFilter={setEligibilityRequirementFilter}
          minimumEducationRequirementFilter={minimumEducationRequirementFilter}
          setMinimumEducationRequirementFilter={setMinimumEducationRequirementFilter}
          minimumAgeFilter={minimumAgeFilter}
          setMinimumAgeFilter={setMinimumAgeFilter}
          industryTagFilter={industryTagFilter}
          setIndustryTagFilter={setIndustryTagFilter}
          setWageMinFilter={setWageMinFilter}
          wageMinFilter={wageMinFilter}
          wageMaxFilter={wageMaxFilter}
          setWageMaxFilter={setWageMaxFilter}
          zipCodeFilter={zipCodeFilter}
          setZipCodeFilter={setZipCodeFilter}
          radiusFilter={radiusFilter}
          setRadiusFilter={setRadiusFilter}
          userLocation={userLocation}
          mobileMapShowing={mobileMapShowing}
          isLargeDisplay={isLargeDisplay}
          setLocationFilter={setLocationFilter}
          setBasicFilter={setBasicFilter}
          parentRef={parentRef}
          onToggleListOrMap={onToggleListOrMap}
        />
      </Col>
    </Row>
  </Form>
}

ProgramSearchMain.propTypes = {
  basicFilter       : PropTypes.string,
  locationFilter    : PropTypes.string,
  onBasicChanged    : PropTypes.func,
  onLocationChanged : PropTypes.func,
}

ProgramSearch.propTypes = {
  counties                     : PropTypes.array,
  eligibilityRequirements      : PropTypes.array,
  industryTags                 : PropTypes.array,
  isLargeDisplay               : PropTypes.bool,
  isMobileDisplay              : PropTypes.bool,
  mapDispatch                  : PropTypes.func,
  minimumEducationRequirements : PropTypes.array,
  mobileMapShowing             : PropTypes.bool,
  onSearchClick                : PropTypes.func,
  parentRef                    : PropTypes.object,
  programLevels                : PropTypes.array,
  programTypes                 : PropTypes.array,
  searchFilters                : PropTypes.object,
  setTypeFilter                : PropTypes.func,
  typeFilter                   : PropTypes.string,
  userLocation                 : PropTypes.object,
}

export default ProgramSearch
